// Topological-sort + layer-assignment auto-layout for workflow graphs
// that arrive WITHOUT meaningful positions (Claude / API callers that
// don't set per-node `position`, so every node lands on `{x: 0, y: 0}`
// and the canvas renders them stacked on top of each other).
//
// We only re-lay-out when positions are degenerate — interactively-
// authored workflows have varied x/y values and we leave those alone.
// (DEV-16181.)

export interface IPositionable {
  nodeKey: string;
  position: { x: number; y: number } | null | undefined;
}

export interface IDirectedEdge {
  fromNodeKey: string;
  toNodeKey: string;
}

/**
 * Returns true when the supplied positions look like "no one ever set
 * these" — all null, all undefined, or every node sharing the exact
 * same (x, y). The Claude/API case is `{x: 0, y: 0}` for every node,
 * which falls through the typical `?? defaultGrid` fallback because the
 * value isn't null. Once you have two different real positions, the
 * user (or the API caller) has expressed intent and we respect it.
 */
export function positionsAreDegenerate(nodes: readonly IPositionable[]): boolean {
  if (nodes.length <= 1) return true;
  const first = nodes[0]?.position ?? null;
  const firstKey = first ? `${first.x},${first.y}` : '__null__';
  for (let i = 1; i < nodes.length; i++) {
    const p = nodes[i]?.position ?? null;
    const key = p ? `${p.x},${p.y}` : '__null__';
    if (key !== firstKey) return false;
  }
  return true;
}

/**
 * Lay nodes out left-to-right by topological layer. Each layer becomes
 * a column; nodes within a layer stack vertically.
 *
 * Layer assignment: standard Kahn-style BFS. A node's layer is one more
 * than the max layer of its predecessors; nodes with no incoming edge
 * start at layer 0. Cycles (which shouldn't reach the editor, the
 * runner rejects them at compile time) get placed at layer 0 too —
 * preferring a wrong visual to crashing.
 */
export function layoutHorizontalByLayer(
  nodes: readonly IPositionable[],
  edges: readonly IDirectedEdge[],
  opts: { xSpacing?: number; ySpacing?: number; xOffset?: number; yOffset?: number } = {},
): Record<string, { x: number; y: number }> {
  const xSpacing = opts.xSpacing ?? 240;
  const ySpacing = opts.ySpacing ?? 140;
  const xOffset = opts.xOffset ?? 40;
  const yOffset = opts.yOffset ?? 40;

  const nodeKeys = nodes.map((n) => n.nodeKey);
  const nodeKeySet = new Set(nodeKeys);
  const incoming = new Map<string, string[]>();
  for (const e of edges) {
    if (!nodeKeySet.has(e.fromNodeKey) || !nodeKeySet.has(e.toNodeKey)) continue;
    const list = incoming.get(e.toNodeKey) ?? [];
    list.push(e.fromNodeKey);
    incoming.set(e.toNodeKey, list);
  }

  // BFS from roots — nodes with no incoming edge.
  const layer = new Map<string, number>();
  const queue: string[] = [];
  for (const key of nodeKeys) {
    if (!incoming.has(key) || (incoming.get(key) ?? []).length === 0) {
      layer.set(key, 0);
      queue.push(key);
    }
  }
  // Nodes inside a cycle never get reached by BFS from roots; fall them
  // back to layer 0 so the canvas still renders them somewhere. The
  // layer cap (`nodes.length`) is a hard cycle guard: in a DAG, no path
  // can exceed N-1 layers, so anything past N is a cycle and we stop
  // pushing rather than spin forever.
  while (queue.length > 0) {
    const next = queue.shift() as string;
    const curLayer = layer.get(next) ?? 0;
    for (const e of edges) {
      if (e.fromNodeKey !== next) continue;
      if (!nodeKeySet.has(e.toNodeKey)) continue;
      const targetLayer = curLayer + 1;
      if (targetLayer >= nodes.length) continue;
      const existing = layer.get(e.toNodeKey);
      if (existing === undefined || existing < targetLayer) {
        layer.set(e.toNodeKey, targetLayer);
        queue.push(e.toNodeKey);
      }
    }
  }
  for (const key of nodeKeys) {
    if (!layer.has(key)) layer.set(key, 0);
  }

  // Group keys by layer index, preserving the source order so the
  // visual output is deterministic for the same input. Within a layer,
  // we stack top-to-bottom in the order the nodes appeared.
  const byLayer = new Map<number, string[]>();
  for (const key of nodeKeys) {
    const l = layer.get(key) ?? 0;
    const list = byLayer.get(l) ?? [];
    list.push(key);
    byLayer.set(l, list);
  }

  const out: Record<string, { x: number; y: number }> = {};
  for (const [l, keys] of byLayer.entries()) {
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i] as string;
      out[key] = {
        x: xOffset + l * xSpacing,
        y: yOffset + i * ySpacing,
      };
    }
  }
  return out;
}
